// Nederlands — juridische en supportteksten.
export default (C) => ({
  ui: {
    header: "JURIDISCH & SUPPORT",
    back: "TERUG",
    nav: "Juridische documenten",
    onThisPage: "OP DEZE PAGINA",
    lastUpdated: "LAATST BIJGEWERKT",
    effective: "29 oktober 2026",
    companyDetails: "BEDRIJFSGEGEVENS",
    cd: { company: "Bedrijf", nipt: "NIPT / Fiscaal nummer", office: "Statutaire zetel & servicepunt", email: "E-mail", phone: "Telefoon" },
  },

  privacy: {
    kicker: "JURIDISCH · PRIVACY",
    title: "Privacyverklaring",
    intro: "Hoe Eagle Inc met uw persoonsgegevens omgaat — op onze websites, in onze diensten en wanneer u ons betaalt met een kaart, Apple Pay, Google Pay, een betaallink of een QR-code.",
    sections: [
      {
        id: "who-we-are",
        title: "Wie wij zijn",
        body: [
          `Deze privacyverklaring legt uit hoe ${C.legalName} ("Eagle Inc", "wij", "ons") persoonsgegevens verzamelt, gebruikt en beschermt wanneer u een van onze websites bezoekt, contact met ons opneemt, een factuur ontvangt of ons betaalt met een kaart, Apple Pay, Google Pay, een betaallink of een QR-code.`,
          "Zij geldt voor alle websites van de Eagle Inc-groep, waaronder eagle-inc.net, eagle-inc.al, eagle-inc.eu, eagle-eyes.al, eagle-commerce.al, digital-services.al, socialdeal.al, bookitnow.al en incontact.al, en voor de diensten die worden aangeboden onder de merken Eagle Eyes (Real Estate, IT Management, Digital Services) en Eagle Commerce.",
          {
            kv: [
              ["Verwerkingsverantwoordelijke", C.legalName],
              ["Handelsnamen", C.tradeNames],
              ["NIPT / Fiscaal nummer", C.nuis],
              ["Statutair adres", C.address],
              ["Contact privacy", C.supportEmail],
              ["Telefoon", C.supportPhone],
            ],
          },
        ],
      },
      {
        id: "legal-framework",
        title: "Wettelijk kader",
        body: [
          "Wij verwerken persoonsgegevens in overeenstemming met de Albanese Wet nr. 124/2024 \"Inzake de bescherming van persoonsgegevens\". Bevindt u zich in de Europese Unie of de Europese Economische Ruimte en is de Algemene Verordening Gegevensbescherming (AVG, Verordening (EU) 2016/679) op onze verwerking van toepassing, dan leven wij ook de AVG na.",
          "De toezichthoudende autoriteit in Albanië is de Commissaris voor het Recht op Informatie en de Bescherming van Persoonsgegevens (Komisioneri për të Drejtën e Informimit dhe Mbrojtjen e të Dhënave Personale), https://idp.al.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Welke persoonsgegevens wij verzamelen",
        body: [
          "Wij verzamelen alleen de gegevens die wij nodig hebben voor de hieronder beschreven doeleinden:",
          {
            list: [
              "Identiteits- en contactgegevens — naam, bedrijfsnaam, e-mailadres, telefoonnummer en post- of factuuradres, die u verstrekt via onze contact- en supportformulieren, per e-mail, telefoon of WhatsApp, of wanneer wij een offerte, contract of factuur opstellen.",
              "Contract- en factuurgegevens — de bestelde diensten, offertes, contracten, facturen, factuurnummers, bedragen, valuta, btw-gegevens en correspondentie over de opdracht.",
              "Betaaltransactiegegevens — betaalstatus, datum, bedrag, valuta, soort betaalmiddel (bijv. Visa, Mastercard, Apple Pay, Google Pay), een autorisatie- of transactiereferentie en, als de bank die verstrekt, een gemaskeerd kaartnummer (bijv. de laatste vier cijfers). Wij ontvangen of bewaren nooit volledige kaartnummers, vervaldatums of CVV/CVC-codes.",
              "Supportgegevens — de inhoud van uw supportverzoeken, terugbetalingsverzoeken en alle informatie die u met ons deelt wanneer wij u helpen.",
              "Technische gegevens — IP-adres, browsertype, apparaatgegevens en verzoeklogs die onze hostinginfrastructuur automatisch vastlegt voor de beveiliging en om de website te leveren.",
            ],
          },
          "Wij verzamelen niet bewust bijzondere categorieën van persoonsgegevens en vragen u ons zulke gegevens niet te sturen.",
        ],
      },
      {
        id: "card-payments",
        title: "Kaartbetalingen, Apple Pay en Google Pay",
        body: [
          "Kaartbetalingen worden verwerkt door onze acquirerende bank, Raiffeisen Bank Sh.A. (Albanië), via haar e-commercedienst RaiAccept. Wanneer u betaalt via een RaiAccept-betaallink of -QR-code, voert u uw kaartgegevens in — of autoriseert u Apple Pay of Google Pay — op de beveiligde betaalpagina van de bank, niet op onze website.",
          {
            list: [
              "Uw kaartgegevens worden versleuteld (TLS) rechtstreeks naar de bank en haar PCI DSS-conforme betaalinfrastructuur verzonden. Eagle Inc ziet, verwerkt of bewaart uw volledige kaartnummer of beveiligingscode niet.",
              "Betalingen kunnen door uw kaartuitgever worden geverifieerd met 3-D Secure (bijv. Visa Secure, Mastercard Identity Check).",
              "Gebruikt u Apple Pay of Google Pay, dan verwerken Apple of Google uw gegevens volgens hun eigen privacybeleid; de bank ontvangt een getokeniseerd kaartnummer, niet uw echte kaartnummer.",
              "Raiffeisen Bank, de kaartschema's (Visa, Mastercard) en uw kaartuitgever treden op als zelfstandige verwerkingsverantwoordelijken voor de gegevens die zij verwerken om de betaling uit te voeren, te beveiligen en af te wikkelen en om fraude te voorkomen.",
              "Wij ontvangen van de bank alleen de informatie die nodig is om de betaling aan uw factuur te koppelen en terugbetalingen en geschillen af te handelen (zie \"Welke persoonsgegevens wij verzamelen\").",
            ],
          },
        ],
      },
      {
        id: "purposes",
        title: "Waarom wij uw gegevens gebruiken en onze rechtsgrond",
        body: [
          {
            kv: [
              ["Beantwoorden van uw vragen", "Maatregelen op uw verzoek voorafgaand aan een overeenkomst; ons gerechtvaardigd belang om berichten te beantwoorden."],
              ["Offertes, contracten en leveren van diensten", "Uitvoering van een overeenkomst met u."],
              ["Facturering en inning van betalingen", "Uitvoering van een overeenkomst; wettelijke verplichting (boekhoud- en belastingwetgeving)."],
              ["Terugbetalingen, chargebacks en betaalgeschillen", "Uitvoering van een overeenkomst; wettelijke verplichting; gerechtvaardigd belang bij het verdedigen van rechtsvorderingen."],
              ["Klantenservice", "Uitvoering van een overeenkomst; gerechtvaardigd belang bij goede dienstverlening."],
              ["Fraudepreventie en websitebeveiliging", "Gerechtvaardigd belang bij de bescherming van onze klanten, ons bedrijf en onze systemen; wettelijke verplichting waar van toepassing."],
              ["Boekhoudkundige, fiscale en wettelijke administratie", "Wettelijke verplichting."],
              ["Onthouden van uw taalkeuze", "Gerechtvaardigd belang bij een functionerende website (alleen opgeslagen in uw eigen browser)."],
            ],
          },
          "Wij verkopen geen persoonsgegevens, gebruiken ze niet voor geautomatiseerde besluitvorming met rechtsgevolgen of vergelijkbare aanzienlijke gevolgen voor u, en sturen alleen marketingberichten als u daarom hebt gevraagd. U kunt zich op elk moment afmelden.",
        ],
      },
      {
        id: "recipients",
        title: "Met wie wij gegevens delen",
        body: [
          "Wij delen persoonsgegevens alleen waar nodig, met:",
          {
            list: [
              "Raiffeisen Bank Sh.A. (Albanië) en de kaartschema's en betaalnetwerken die betrokken zijn bij de verwerking van uw betaling, en Apple of Google wanneer u hun wallet gebruikt.",
              "Microsoft (Microsoft Azure-hosting en Microsoft 365 e-mail- en productiviteitsdiensten) — als onze verwerker.",
              "MongoDB Inc. (MongoDB Atlas-databasehosting voor via de website verzonden formulieren) — als onze verwerker.",
              "GitHub (hosting van broncode en buildpijplijn; daar worden geen klantgegevens opgeslagen).",
              "Onze accountants, auditors en juridisch adviseurs, onder geheimhoudingsplicht.",
              "Overheidsinstanties, rechtbanken en opsporingsinstanties wanneer wij daartoe wettelijk verplicht zijn.",
            ],
          },
          "Elke verwerker is gebonden aan een schriftelijke overeenkomst en mag uw gegevens alleen volgens onze instructies gebruiken.",
        ],
      },
      {
        id: "transfers",
        title: "Internationale doorgifte",
        body: [
          "Sommige van onze dienstverleners (zoals Microsoft en MongoDB) kunnen gegevens verwerken in de Europese Unie of in andere landen buiten Albanië. Wanneer gegevens worden doorgegeven aan een land dat geen passend beschermingsniveau biedt, baseren wij ons op passende waarborgen uit Wet nr. 124/2024 en, waar van toepassing, de AVG — zoals standaardcontractbepalingen — en op de eigen beveiligingsverplichtingen van de dienstverleners.",
        ],
      },
      {
        id: "retention",
        title: "Hoe lang wij gegevens bewaren",
        body: [
          {
            list: [
              "Vragen en supportverzoeken die niet tot een overeenkomst leiden: tot 24 maanden na het laatste contact.",
              "Contracten, facturen, betalings- en boekhoudgegevens: gedurende de termijn die de Albanese boekhoud- en belastingwetgeving voorschrijft.",
              "Gegevens die nodig zijn voor een geschil, terugbetaling of chargeback: totdat de zaak volledig is afgehandeld en een eventuele verjaringstermijn is verstreken.",
              "Technische loggegevens: in de regel niet langer dan 90 dagen, tenzij nodig voor het onderzoek naar een beveiligingsincident.",
            ],
          },
          "Na deze termijnen worden gegevens verwijderd of geanonimiseerd.",
        ],
      },
      {
        id: "cookies",
        title: "Cookies en lokale opslag",
        body: [
          "Onze websites gebruiken geen advertentie- of trackingcookies. Wij slaan één item op in de lokale opslag van uw browser — de door u gekozen weergavetaal — zodat de website die onthoudt. Dit item verlaat uw apparaat nooit en u kunt het op elk moment via uw browserinstellingen verwijderen.",
          "Om onze lettertypen weer te geven, laadt uw browser lettertypen van Google Fonts en Fontshare, die daarbij uw IP-adres ontvangen. De betaalpagina van RaiAccept, beheerd door de bank, kan eigen strikt noodzakelijke cookies gebruiken voor beveiliging en fraudepreventie.",
        ],
      },
      {
        id: "security",
        title: "Hoe wij uw gegevens beschermen",
        body: [
          "Al onze websites worden uitsluitend via HTTPS aangeboden. Gegevens worden opgeslagen bij gerenommeerde cloudproviders, toegang is beperkt tot bevoegde personen met meervoudige authenticatie en wij passen het principe van minimale rechten toe. Kaartgegevens bereiken onze systemen nooit.",
          "Doet zich een inbreuk in verband met persoonsgegevens voor die waarschijnlijk een risico voor uw rechten oplevert, dan melden wij dit binnen 72 uur aan de Commissaris en informeren wij u zonder onnodige vertraging wanneer het risico hoog is, zoals de wet voorschrijft.",
        ],
      },
      {
        id: "your-rights",
        title: "Uw rechten",
        body: [
          "Onder de voorwaarden van de wet hebt u het recht om:",
          {
            list: [
              "geïnformeerd te worden over en inzage te krijgen in de persoonsgegevens die wij van u hebben;",
              "onjuiste gegevens te laten corrigeren en onvolledige gegevens te laten aanvullen;",
              "uw gegevens te laten wissen (\"recht om vergeten te worden\");",
              "de verwerking van uw gegevens te laten beperken;",
              "uw gegevens te ontvangen in een gestructureerd, machineleesbaar formaat (gegevensoverdraagbaarheid);",
              "bezwaar te maken tegen verwerking op basis van ons gerechtvaardigd belang, en op elk moment tegen direct marketing;",
              "een gegeven toestemming in te trekken, zonder dat dit eerdere verwerking aantast.",
            ],
          },
          `Om uw rechten uit te oefenen, mailt u naar ${C.supportEmail} of schrijft u naar ${C.legalName}, ${C.address}. Wij kunnen u vragen uw identiteit te bevestigen en reageren binnen de wettelijke termijnen (normaal gesproken binnen één maand).`,
          "U hebt ook het recht een klacht in te dienen bij de Commissaris voor het Recht op Informatie en de Bescherming van Persoonsgegevens (https://idp.al) of, in de EU/EER, bij de gegevensbeschermingsautoriteit van uw land van verblijf.",
        ],
      },
      {
        id: "children",
        title: "Kinderen",
        body: ["Onze diensten zijn bedoeld voor bedrijven en volwassenen. Wij verzamelen niet bewust persoonsgegevens van kinderen jonger dan 16 jaar."],
      },
      {
        id: "changes",
        title: "Wijzigingen in deze verklaring",
        body: [
          "Wij kunnen deze privacyverklaring van tijd tot tijd bijwerken. De actuele versie staat altijd op /privacy, met de datum van de laatste wijziging bovenaan de pagina. Belangrijke wijzigingen melden wij per e-mail aan bestaande klanten.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        body: [
          { kv: [["Bedrijf", C.legalName], ["Adres", C.address], ["E-mail", C.supportEmail], ["Telefoon", C.supportPhone], ["Support", "/support"]] },
        ],
      },
    ],
  },

  terms: {
    kicker: "JURIDISCH · GEBRUIKSVOORWAARDEN",
    title: "Gebruiksvoorwaarden",
    intro: "De voorwaarden die gelden voor onze websites, onze diensten en betalingen aan Eagle Inc met een kaart, Apple Pay, Google Pay, een betaallink of een QR-code — inclusief ons annulerings- en terugbetalingsbeleid.",
    sections: [
      {
        id: "merchant",
        title: "Gegevens van de verkoper",
        body: [
          "Deze gebruiksvoorwaarden zijn van toepassing op het gebruik van de websites van de Eagle Inc-groep en op de aankoop van en betaling voor onze diensten. De verkoper en ontvanger van alle betalingen is:",
          {
            kv: [
              ["Verkoper", C.legalName],
              ["Handelsnamen", C.tradeNames],
              ["NIPT / Fiscaal nummer", C.nuis],
              ["Statutair adres", C.address],
              ["Land", "Albanië"],
              ["Klantenservice", `${C.supportEmail} · ${C.supportPhone}`],
              ["Supportpagina", C.supportUrl],
            ],
          },
          "Door onze websites te gebruiken, een offerte te accepteren of een door ons uitgegeven factuur, betaallink of QR-code te betalen, gaat u akkoord met deze voorwaarden. Gaat u niet akkoord, gebruik onze websites of diensten dan niet.",
        ],
      },
      {
        id: "services",
        title: "Onze diensten",
        body: [
          "Eagle Inc biedt onder haar merken professionele diensten aan, waaronder IT-management en IT-consultancy (cloud, infrastructuur, Microsoft 365/Azure, portfoliomanagement), digitale diensten en softwareproducten, en diensten op het gebied van woonvastgoed in Tirana.",
          "Onze diensten zijn immaterieel: er vindt geen fysieke verzending plaats. De precieze scope, opleveringen, acceptatiecriteria, planning en prijs van elke opdracht staan in de schriftelijke offerte, het voorstel, het contract of de factuur die wij u sturen (de \"Opdracht\"). Is een Opdracht in strijd met deze voorwaarden, dan gaat de Opdracht voor.",
        ],
      },
      {
        id: "orders",
        title: "Offertes, opdrachten en overeenkomst",
        body: [
          {
            list: [
              "Een overeenkomst komt tot stand wanneer u onze schriftelijke offerte of ons voorstel accepteert (per e-mail, met een handtekening of door de bijbehorende factuur of betaallink te betalen).",
              "Offertes zijn 30 dagen geldig, tenzij anders vermeld.",
              "Eenmalige diensten en oplossingen worden bij de start van de opdracht gefactureerd, tenzij de Opdracht anders bepaalt.",
              "Doorlopende diensten (zoals beheerde IT en portfoliomanagement) worden geleverd onder een jaarcontract met maandelijkse facturering, zoals vastgelegd in de Opdracht.",
              "U bent verantwoordelijk voor juiste informatie, tijdige toegang tot systemen en de medewerking die wij redelijkerwijs nodig hebben om de dienst te leveren.",
            ],
          },
        ],
      },
      {
        id: "prices",
        title: "Prijzen en valuta",
        body: [
          "Prijzen staan in de Opdracht en op de factuur of betaallink, in Albanese lek (ALL) of euro (EUR). Het bedrag en de valuta op de betaalpagina van RaiAccept zijn het bedrag en de valuta die bij u in rekening worden gebracht.",
          "Tenzij uitdrukkelijk anders vermeld, zijn prijzen exclusief btw, die wordt toegevoegd waar de Albanese belastingwetgeving dat voorschrijft. Luidt uw kaart in een andere valuta, dan kan uw kaartuitgever een eigen wisselkoers en kosten hanteren; Eagle Inc heeft daar geen invloed op.",
        ],
      },
      {
        id: "payment",
        title: "Betaalmethoden en betaalveiligheid",
        body: [
          "Wij accepteren de volgende betaalmethoden:",
          {
            list: [
              "creditcards en debitcards van Visa en Mastercard;",
              "Apple Pay en Google Pay, als uw apparaat en kaart dit ondersteunen;",
              "betaallinks en QR-codes die via RaiAccept zijn aangemaakt en op onze facturen staan of u per e-mail of bericht worden gestuurd;",
              "bankoverschrijving naar de rekening die op de factuur staat.",
            ],
          },
          "Betalingen met een kaart, Apple Pay en Google Pay worden verwerkt door onze acquirerende bank, Raiffeisen Bank Sh.A. (Albanië), via haar e-commercedienst RaiAccept. Kaartgegevens worden uitsluitend ingevoerd op de beveiligde, PCI DSS-conforme betaalpagina van de bank via een versleutelde (TLS) verbinding. Eagle Inc ziet of bewaart nooit uw volledige kaartnummer, vervaldatum of beveiligingscode. Voor betalingen kan 3-D Secure-verificatie door uw kaartuitgever nodig zijn.",
          "Een betaallink of QR-code is gekoppeld aan een specifieke factuur en een specifiek bedrag. Controleer altijd vóór het betalen of de naam van de verkoper op de betaalpagina Eagle Inc is. Is een link verlopen of werkt hij niet, neem dan contact op met support; wij sturen u een nieuwe.",
          "Betaling dient te geschieden op de datum die op de factuur staat. Wij kunnen doorlopende diensten opschorten als een factuur na een schriftelijke herinnering onbetaald blijft.",
          { note: `Eagle Inc zal u nooit vragen uw volledige kaartnummer, vervaldatum of beveiligingscode te sturen per e-mail, telefoon, chat of een ander kanaal. Ontvangt u zo'n verzoek uit onze naam, reageer dan niet en meld het aan ${C.supportEmail}.` },
        ],
      },
      {
        id: "delivery",
        title: "Levering van diensten",
        body: [
          "Diensten worden op afstand en/of op locatie geleverd, zoals beschreven in de Opdracht. De levering begint op de afgesproken startdatum of na ontvangst van de betaling wanneer vooruitbetaling vereist is. Opleveringen (zoals rapporten, configuraties, documentatie of toegang tot software) worden elektronisch of op locatie geleverd. U ontvangt een bevestiging per e-mail wanneer een betaalde dienst is geleverd of een mijlpaal is bereikt.",
        ],
      },
      {
        id: "cancellation",
        title: "Annulerings- en terugbetalingsbeleid",
        body: [
          {
            list: [
              "Vóór de start van het werk: u kunt een Opdracht op elk moment schriftelijk annuleren voordat wij met de dienst beginnen en ontvangt het volledige betaalde bedrag terug.",
              "Na de start van het werk: u kunt schriftelijk annuleren; u betaalt voor het verrichte werk en de gemaakte kosten tot de annuleringsdatum, en een vooruitbetaald bedrag voor nog niet verricht werk wordt terugbetaald.",
              "Jaarcontracten met maandelijkse facturering kunnen worden beëindigd zoals in het contract bepaald. Reeds gefactureerde en geleverde maanden worden niet terugbetaald, tenzij de dienst niet is geleverd.",
              "Dubbele of onjuiste afschrijvingen: bent u twee keer of een onjuist bedrag in rekening gebracht, dan betalen wij het teveel betaalde volledig terug.",
              "Diensten niet geleverd zoals afgesproken: leveren wij een dienst niet volgens de Opdracht en kunnen wij dat niet binnen een redelijke termijn herstellen, dan hebt u recht op een volledige of gedeeltelijke terugbetaling.",
            ],
          },
          `Zo vraagt u een terugbetaling aan: mail naar ${C.supportEmail} of gebruik het formulier op /support, met uw factuurnummer, betaaldatum, bedrag en de reden. Wij bevestigen de ontvangst binnen 1 werkdag en beslissen binnen 5 werkdagen.`,
          "Goedgekeurde terugbetalingen worden gedaan op dezelfde kaart of hetzelfde betaalmiddel als de oorspronkelijke betaling (inclusief Apple Pay of Google Pay) — wij betalen kaartbetalingen niet contant of op een andere kaart terug. Wij starten de terugbetaling binnen 5 werkdagen na goedkeuring; afhankelijk van uw kaartuitgever kan het tot 10 werkdagen duren voordat het bedrag op uw afschrift staat.",
          "Handelt u als consument, dan gelden de dwingende consumentenbeschermingsregels van het Albanese recht, inclusief een eventueel wettelijk herroepingsrecht voor overeenkomsten op afstand, naast dit beleid en worden zij er niet door beperkt.",
        ],
      },
      {
        id: "disputes",
        title: "Betaalgeschillen en chargebacks",
        body: [
          `Herkent u een afschrijving van Eagle Inc niet of bent u het niet eens met een bedrag, neem dan eerst contact met ons op via ${C.supportEmail} — de meeste kwesties zijn snel op te lossen en direct terug te betalen. Dit laat uw recht om een transactie bij uw kaartuitgever te betwisten onverlet. Wordt een chargeback ingediend, dan werken wij mee met de bank en verstrekken wij de betreffende Opdracht, factuur en leveringsdocumentatie.`,
        ],
      },
      {
        id: "website-use",
        title: "Gebruik van onze websites",
        body: [
          "U mag onze websites alleen voor rechtmatige doeleinden gebruiken. U mag niet proberen onbevoegd toegang te krijgen tot onze systemen, de werking van de websites verstoren, valse informatie invoeren of onze formulieren misbruiken. Wij kunnen elk onderdeel van de websites op elk moment wijzigen, opschorten of beëindigen.",
          "Alle inhoud op onze websites — waaronder teksten, logo's, het Eagle-medaillon, ontwerpen, afbeeldingen en software — is eigendom van of in licentie bij Eagle Inc en beschermd door het intellectuele-eigendomsrecht. U mag deze niet kopiëren of hergebruiken zonder onze voorafgaande schriftelijke toestemming.",
          "Informatie op onze websites is algemeen en kan veranderen. Zij vormt geen aanbod, tenzij bevestigd in een Opdracht. Links naar websites van derden zijn er voor uw gemak; wij zijn niet verantwoordelijk voor de inhoud daarvan.",
        ],
      },
      {
        id: "liability",
        title: "Aansprakelijkheid",
        body: [
          "Wij voeren onze diensten zorgvuldig en volgens goed vakmanschap uit. Voor zover de wet dat toestaat, is onze totale aansprakelijkheid uit een Opdracht beperkt tot het bedrag dat u onder die Opdracht hebt betaald in de twaalf maanden voorafgaand aan de gebeurtenis die tot de vordering leidt, en zijn wij niet aansprakelijk voor indirecte schade of gevolgschade, waaronder gederfde winst of verlies van gegevens.",
          "Niets in deze voorwaarden sluit aansprakelijkheid uit of beperkt aansprakelijkheid die volgens het toepasselijke recht niet kan worden uitgesloten of beperkt, waaronder aansprakelijkheid voor opzet of grove nalatigheid, of uw dwingende rechten als consument.",
        ],
      },
      {
        id: "privacy",
        title: "Privacy",
        body: ["Wij verwerken persoonsgegevens in overeenstemming met onze privacyverklaring, te vinden op /privacy."],
      },
      {
        id: "force-majeure",
        title: "Overmacht",
        body: ["Geen van beide partijen is aansprakelijk voor het niet of te laat nakomen van verplichtingen door omstandigheden buiten haar redelijke controle, zoals natuurrampen, stroom- of netwerkstoringen bij externe leveranciers, oorlog, stakingen of overheidsingrijpen. De verplichtingen worden opgeschort zolang zo'n omstandigheid duurt."],
      },
      {
        id: "law",
        title: "Toepasselijk recht en geschillen",
        body: [
          "Op deze voorwaarden en elke Opdracht is het recht van de Republiek Albanië van toepassing. Wij proberen klachten altijd eerst in der minne op te lossen — neem contact op met support. Geschillen die niet in der minne kunnen worden opgelost, vallen onder de bevoegdheid van de bevoegde rechtbank in Tirana, Albanië, onverminderd het dwingende recht van een consument om een procedure te beginnen bij de rechter van zijn woonplaats.",
        ],
      },
      {
        id: "changes",
        title: "Wijzigingen in deze voorwaarden",
        body: [
          "Wij kunnen deze voorwaarden van tijd tot tijd wijzigen. Op een Opdracht is de versie van toepassing die op de datum van uw Opdracht op /termsofuse staat. De datum van de laatste wijziging staat bovenaan deze pagina.",
        ],
      },
    ],
  },

  support: {
    kicker: "KLANTENSERVICE",
    title: "Support",
    intro: "Hulp bij betalingen, facturen, terugbetalingen en onze diensten. Ons supportteam beantwoordt elk verzoek persoonlijk.",
    tocContact: "Contact met de klantenservice",
    tocRequest: "Een supportverzoek sturen",
    email: "E-mail",
    phone: "Telefoon",
    hoursLabel: "Openingstijden support",
    hours: "Maandag – vrijdag, 09:00 – 17:00 (CET/CEST, tijd in Tirana). Eerste reactie binnen 1 werkdag.",
    security: "Stuur ons voor uw veiligheid nooit uw volledige kaartnummer, vervaldatum of beveiligingscode (CVV/CVC). Om een betaling te herkennen zijn het factuurnummer, de datum, het bedrag en de laatste vier cijfers van uw kaart voldoende.",
    requestTitle: "Een supportverzoek sturen",
    faqTitle: "VEELGESTELDE VRAGEN",
    topics: [
      "Betaalprobleem (geweigerd, mislukt, verlopen link of QR-code)",
      "Verzoek om terugbetaling",
      "Dubbele of onjuiste afschrijving",
      "Vraag over een factuur",
      "Vraag over een dienst of opdracht",
      "Privacy / verzoek over persoonsgegevens",
      "Overig",
    ],
    form: {
      name: "Volledige naam",
      email: "E-mailadres",
      reference: "Factuurnummer of referentie (optioneel)",
      message: "Hoe kunnen wij helpen? Vermeld nooit uw volledige kaartnummer of beveiligingscode.",
      consent: "Door dit formulier te versturen gaat u ermee akkoord dat wij uw gegevens verwerken om uw verzoek af te handelen, zoals beschreven in /privacy.",
      send: "VERZOEK VERSTUREN",
      sending: "BEZIG MET VERSTUREN…",
      sentTitle: "Dank u — uw verzoek is ontvangen.",
      sentBody: (email) => `Wij reageren binnen 1 werkdag op ${email}.`,
      error: `Uw bericht kon niet worden verstuurd. Mail naar ${C.supportEmail} of bel ${C.supportPhone}.`,
    },
    faq: [
      {
        id: "pay-how",
        title: "Hoe betaal ik een factuur?",
        body: [
          "Elke factuur bevat een beveiligde RaiAccept-betaallink en QR-code van Raiffeisen Bank Albania. Klik op de link of scan de QR-code met de camera van uw telefoon, controleer het bedrag en of de verkoper Eagle Inc is, en betaal met Visa, Mastercard, Apple Pay of Google Pay. U kunt ook per bankoverschrijving betalen naar de rekening die op de factuur staat.",
        ],
      },
      {
        id: "pay-failed",
        title: "Mijn betaling is geweigerd of mislukt",
        body: [
          {
            list: [
              "Controleer of de kaartgegevens, vervaldatum en factuurgegevens juist zijn en of online/internationale betalingen op uw kaart zijn ingeschakeld.",
              "Rond de 3-D Secure-verificatie af in uw bankapp of met een sms-code — de betaling mislukt als deze stap verloopt.",
              "Zorg dat er voldoende saldo of limiet op de kaart beschikbaar is.",
              "Probeer Apple Pay of Google Pay, een andere kaart, of neem contact op met uw kaartuitgever — weigeringen worden bepaald door de uitgevende bank, die u de reden kan vertellen.",
            ],
          },
          "Een geweigerde betaling wordt niet afgeschreven. Staat na een mislukte poging een bedrag gereserveerd (in behandeling) op uw rekening, dan geeft uw bank dat automatisch vrij, meestal binnen enkele werkdagen.",
        ],
      },
      {
        id: "pay-link",
        title: "De betaallink of QR-code is verlopen of opent niet",
        body: ["Betaallinks en QR-codes zijn beperkt geldig en gelden voor één specifiek bedrag. Neem contact met ons op met uw factuurnummer; wij sturen u binnen 1 werkdag een nieuwe link."],
      },
      {
        id: "double",
        title: "Er is twee keer of een verkeerd bedrag afgeschreven",
        body: ["Neem contact met ons op met uw factuurnummer, de datum en het bedrag van elke afschrijving en de laatste vier cijfers van uw kaart. Dubbele of onjuiste afschrijvingen worden volledig teruggestort op de oorspronkelijke kaart."],
      },
      {
        id: "refund",
        title: "Hoe vraag ik een terugbetaling aan?",
        body: [
          `Gebruik het formulier hierboven of mail naar ${C.supportEmail} met uw factuurnummer, betaaldatum, bedrag en de reden van uw verzoek. Wij bevestigen de ontvangst binnen 1 werkdag en beslissen binnen 5 werkdagen. Goedgekeurde terugbetalingen gaan naar dezelfde kaart of wallet als de betaling en worden binnen 5 werkdagen gestart; uw kaartuitgever kan tot 10 werkdagen nodig hebben om het bedrag op uw afschrift te tonen. Het volledige beleid staat in onze gebruiksvoorwaarden: /termsofuse.`,
        ],
      },
      {
        id: "receipt",
        title: "Waar vind ik mijn betaalbewijs of factuur?",
        body: ["Na een geslaagde betaling ontvangt u een betaalbevestiging van de betaalpagina. Wij sturen de factuur en, op verzoek, een betaalbevestiging naar het e-mailadres dat bij ons bekend is. Vraag support als u een kopie nodig hebt."],
      },
      {
        id: "complaint",
        title: "Klachten en escalatie",
        body: [
          `Bent u niet tevreden over de afhandeling van uw verzoek, antwoord dan op ons bericht met het verzoek om escalatie — uw klacht wordt beoordeeld door de directie van ${C.legalName} en binnen 10 werkdagen beantwoord. U behoudt altijd het recht om contact op te nemen met uw kaartuitgever of de bevoegde autoriteiten.`,
        ],
      },
    ],
  },
});
